import Collapse from '@material-ui/core/Collapse'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import { Box, List, ListItemButton, ListItemIcon, Tooltip, Typography } from '@mui/material'
import { styled } from '@mui/system'
import React, { useEffect, useState } from 'react'

import { ReactComponent as UpgradePlanIconSelected } from '../icons/upgrade-plan-icon-selected.svg'
import { ReactComponent as UpgradePlanIcon } from '../icons/upgrade-plan-icon.svg'

const NavigationListItemButton = styled(ListItemButton)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  cursor: 'pointer',

  '& svg path': {
    color: theme.palette.grey[450],
  },

  '&:active': {
    '& p': {
      color: theme.palette.grey[200],
    },

    '& svg path': {
      color: theme.palette.grey[200],
    },
  },

  '&.Mui-selected': {
    color: theme.palette.grey[200],

    '& p': {
      color: theme.palette.grey[200],
    },

    '& svg path': {
      fill: theme.palette.grey[200],
    },

    '&:hover': {
      cursor: 'default',
      opacity: 1,
    },
  },

  '& .MuiListItemIcon-root': {
    minWidth: 0,
    marginRight: theme.spacing(1.5),
  },
}))

interface IDropdownItem {
  name: string
  onClick?: () => void
  selected?: boolean
  isHidden?: boolean
  isComingSoon?: boolean
  isFeatureEnabled?: boolean
  isDisabled?: boolean
  hasAiFunctionality?: boolean
}

interface IDropdownItems {
  [key: string]: IDropdownItem
}

interface INavigationItem {
  name: string
  onClick?: () => void
  selected?: boolean
  isHidden?: boolean
  icon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>
  iconSelected?: any
  dropdownItems?: IDropdownItems | false
  isSubNavOpen?: boolean
  isComingSoon?: boolean
  isFeatureEnabled?: boolean
  isDisabled?: boolean
  hasAiFunctionality?: boolean
  isSidebarOpen?: boolean
  type?: string
}

interface INavigationItems {
  [key: string]: INavigationItem
}

interface NavigationItemsProps {
  navigationItems: INavigationItems
  isSidebarOpen?: boolean
}

function DropdownItem(props: IDropdownItem) {
  const { name, onClick, selected, isComingSoon, isFeatureEnabled, isDisabled, hasAiFunctionality } = props

  return (
    <Tooltip title={isComingSoon ? 'Coming soon' : ''} placement="right">
      <ListItemButton
        key={name}
        onClick={onClick}
        selected={selected}
        sx={{
          marginBottom: 0.5,
          paddingY: 1,
          paddingX: 1.75,
          borderRadius: '6px',
          '&:first-of-type': {
            marginTop: 0.25,
          },
          '&:last-of-type': {
            marginBottom: 1.5,
          },
          '&:hover': {
            opacity: 1,
            cursor: onClick ? 'pointer' : 'default',
            backgroundColor: isDisabled ? 'transparent' : 'rgba(66, 66, 66, 0.2)',
            '& p': {
              color: isDisabled ? 'grey.600' : 'grey.300',
            },
          },
          '&.Mui-selected': {
            position: 'relative',
            backgroundColor: 'rgba(66, 66, 66, 0.4)',
            '&:hover': {
              backgroundColor: 'rgba(66, 66, 66, 0.4)',
              opacity: 1,
            },
            '&::after': {
              position: 'absolute',
              content: '""',
              top: '50%',
              left: '21px',
              width: '6px',
              height: '6px',
              backgroundColor: 'secondary.500',
              borderRadius: '50%',
              transform: 'translateY(-50%)',
            },
          },
        }}
      >
        <Typography
          variant="body2"
          color={isDisabled ? 'grey.600' : 'grey.400'}
          pl={4}
          sx={{ fontFamily: 'Manrope', fontWeight: 500 }}
        >
          {name}{' '}
          {hasAiFunctionality && (
            <span
              style={{
                backgroundColor: 'rgba(147, 176, 143, 0.2)',
                borderRadius: '200px',
                color: isDisabled ? 'rgb(138 141 135)' : '#CFD1CD',
                padding: '2px 5px',
                fontFamily: 'Mulish',
                fontWeight: 600,
                marginLeft: '7px',
              }}
            >
              AI
            </span>
          )}
        </Typography>

        {!isFeatureEnabled && <UpgradePlanIcon style={{ marginLeft: 'auto', flexShrink: 0 }} />}
      </ListItemButton>
    </Tooltip>
  )
}

function NavigationItem(props: INavigationItem) {
  const {
    name,
    icon,
    iconSelected,
    dropdownItems,
    selected,
    onClick,
    isHidden,
    isComingSoon,
    isFeatureEnabled,
    isDisabled,
    isSidebarOpen,
  } = props
  const Icon = icon
  const IconSelected = iconSelected

  const [openDropdown, setOpenDropdown] = useState(false)

  const handleDropdownClick = () => {
    setOpenDropdown && setOpenDropdown(!openDropdown)
  }

  useEffect(() => {
    if (selected && dropdownItems) {
      setOpenDropdown(true)
    }
  }, [selected])
  return (
    <>
      <Tooltip title={isComingSoon ? 'Coming soon' : ''} placement="right">
        <NavigationListItemButton
          key={name}
          onClick={() => {
            if (onClick) {
              dropdownItems ? handleDropdownClick() : onClick()
            }
          }}
          selected={selected}
          sx={{
            marginBottom: isSidebarOpen ? 0.5 : '',
            paddingY: isSidebarOpen ? 1.25 : 1.75,
            paddingX: 1.5,

            borderRadius: isSidebarOpen ? '6px' : '0px',
            '&:hover': {
              opacity: 1,
              cursor: onClick ? 'pointer' : 'default',
              backgroundColor: !isDisabled ? 'rgba(66, 66, 66, 0.2)' : 'transparent',
              '& p': {
                color: isDisabled ? 'grey.600' : 'grey.300',
              },
            },
            '&.Mui-selected': {
              backgroundColor: isSidebarOpen ? 'primary.600' : 'grey.800',
              '&:hover': {
                backgroundColor: isSidebarOpen ? 'primary.600' : 'grey.800',
              },
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', width: '100%' }}>
            <ListItemIcon>
              {selected && iconSelected ? (
                <IconSelected />
              ) : (
                Icon && (
                  <Icon
                    style={{
                      fill: isDisabled ? '#525252' : selected ? 'black' : 'currentColor',
                    }}
                  />
                )
              )}
            </ListItemIcon>

            {isSidebarOpen && (
              <Typography variant="body2" sx={{ fontWeight: 500, color: isDisabled ? 'grey.600' : 'grey.400' }}>
                {name}
              </Typography>
            )}

            {dropdownItems && isFeatureEnabled && isSidebarOpen && (
              <ArrowForwardIosIcon
                sx={{
                  marginLeft: 'auto',
                  width: '17px',
                  height: '17px',
                  transform: openDropdown ? 'rotate(90deg)' : '',
                }}
              />
            )}

            {!isFeatureEnabled && isSidebarOpen && (
              <ListItemIcon
                sx={{
                  ml: 'auto',
                  mr: '0px !important',
                }}
              >
                {selected ? <UpgradePlanIconSelected /> : <UpgradePlanIcon />}
              </ListItemIcon>
            )}
          </Box>
        </NavigationListItemButton>
      </Tooltip>

      {dropdownItems && isSidebarOpen && (
        <Collapse in={openDropdown} timeout="auto" unmountOnExit>
          <List sx={{ padding: 0 }}>
            {Object.keys(dropdownItems).map((dropdownKey) => {
              const dropdownItem = dropdownItems && dropdownItems[dropdownKey]

              return (
                !isHidden &&
                dropdownItem && (
                  <DropdownItem
                    key={dropdownItem.name}
                    name={dropdownItem.name}
                    onClick={dropdownItem.onClick}
                    selected={dropdownItem.selected}
                    isComingSoon={dropdownItem.isComingSoon}
                    isFeatureEnabled={dropdownItem.isFeatureEnabled}
                    isDisabled={dropdownItem.isDisabled}
                    hasAiFunctionality={dropdownItem.hasAiFunctionality}
                  />
                )
              )
            })}
          </List>
        </Collapse>
      )}
    </>
  )
}

export function NavigationItems(props: NavigationItemsProps) {
  const { navigationItems, isSidebarOpen } = props

  return (
    <List sx={{ padding: 0, maxWidth: isSidebarOpen ? 'auto' : '54px', display: 'flex', flexDirection: 'column' }}>
      {Object.keys(navigationItems).map((navigationKey) => {
        const navigationItem = navigationItems[navigationKey]
        const isCategory = navigationItem.type === 'category'

        return isCategory
          ? isSidebarOpen && (
              <Typography
                key={navigationItem.name}
                variant="caption2"
                fontWeight={700}
                color="grey.450"
                mb={1.5}
                mt={1}
                sx={{ textTransform: 'uppercase' }}
              >
                {navigationItem.name}
              </Typography>
            )
          : !navigationItem.isHidden && (
              <NavigationItem
                key={navigationItem.name}
                name={navigationItem.name}
                selected={navigationItem.selected}
                icon={navigationItem.icon}
                iconSelected={navigationItem.iconSelected}
                isHidden={navigationItem.isHidden}
                onClick={navigationItem.onClick}
                dropdownItems={navigationItem.dropdownItems}
                isComingSoon={navigationItem.isComingSoon}
                isFeatureEnabled={navigationItem.isFeatureEnabled}
                isDisabled={navigationItem.isDisabled}
                isSidebarOpen={isSidebarOpen}
              />
            )
      })}
    </List>
  )
}
